import { LandingPageData, TrendingAsset, DiscountedAssetsResponse } from '@/types/main'
import { BaseService } from './api'

/**
 * An error that still knows what the server said.
 *
 * `getLandingPageData` used to end in `throw new Error('Failed to load landing
 * page data')`, which threw away the status code that `BaseService.request`
 * had carefully attached. The caller could not tell a 403 ("you are not
 * entitled to this") from a 500 ("we are broken") — and those two need
 * completely different UI.
 */
export class LandingPageError extends Error {
  readonly status: number | null

  constructor(message: string, status: number | null) {
    super(message)
    this.name = 'LandingPageError'
    this.status = status
  }
}

const readStatus = (error: unknown): number | null => {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status
    if (typeof status === 'number') return status
  }
  return null
}

class MainScreenService extends BaseService {
  /**
   * The dashboard's two list feeds.
   *
   * 🔴 Both of these carry money — `/orderbook/trending` serializes
   * `offered_amount` and `/orderbook/discounted` serializes a discount against
   * a valuation — and NEITHER has a non-financial counterpart. So this method
   * is not called at all for a session that has not completed verification;
   * see `useMainScreen`. There is no "hide it afterwards" path here on purpose.
   *
   * Two legs, not four. The `/orderbook/stats` leg duplicated `useOtcStats`
   * (which the dashboard already calls, with its own cache and refetch
   * interval) and the `/orderbook` leg populated `latestOrders`, which nothing
   * has ever read — both tables on the dashboard pass `dataKey="orders"` and
   * take their rows from `useOTC`. Two requests deleted, no behaviour lost.
   *
   * Each leg catches its own failure. A dead trending feed used to blank the
   * whole page, discounts included.
   */
  async getLandingPageData(): Promise<LandingPageData> {
    let firstError: unknown = null

    const [trending, discounted] = await Promise.all([
      this.get<{ data: TrendingAsset[] }>('/orderbook/trending').catch((error) => {
        console.warn('Failed to fetch trending assets:', error)
        firstError = firstError ?? error
        return null
      }),
      this.get<DiscountedAssetsResponse>('/orderbook/discounted').catch((error) => {
        console.warn('Failed to fetch discounted assets:', error)
        firstError = firstError ?? error
        return null
      }),
    ])

    // Only a total failure is an error. One live feed is still a usable page.
    if (trending === null && discounted === null) {
      throw new LandingPageError(
        firstError instanceof Error ? firstError.message : 'Failed to load landing page data',
        readStatus(firstError),
      )
    }

    return {
      trendingAssets: trending?.data ?? [],
      discountedAssets: discounted?.data ?? [],
    }
  }
}

export const mainScreenService = new MainScreenService()
